import { Injectable, Type, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBase } from '../definitions/forms/form.definitions';
import { DialogBaseViewComponent } from '../views/dialog-base-view.component';

//For Reference
//https://blog.angular-university.io/angular-material-dialog/

export type DialogData<T> = T extends DialogBaseViewComponent<infer U> ? U : never;
export type DialogVoidy<T> = T extends DialogBaseViewComponent<infer U> ? [void] extends [U] ? T : [null] extends [U] ? T : [undefined] extends [U] ? T : never : never;

@Injectable({
    providedIn: 'root',
})
export class DialogManager {
    private _dialogReference?: MatDialogRef<any>;

    constructor(private _MatDialog: MatDialog) {
    }

    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<DialogVoidy<T>>, data: void, viewContainerRef?: ViewContainerRef | null): T;
    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<T>, data: DialogData<T>, viewContainerRef?: ViewContainerRef | null): T;
    CreateDialog<T extends DialogBaseViewComponent<any>>(componentType: Type<T>, data: DialogData<T>, viewContainerRef?: ViewContainerRef | null) {
        FormBase.ignoreCreatedForms = true;

        if (viewContainerRef === null) {
            viewContainerRef = undefined;
        } else if (viewContainerRef === undefined) {
            console.warn('Calling CreateDialog without a ViewContainerRef does not properly propagate Scope. Please call this.CreateDialog if ' +
                        'in a BaseViewComponent, pass in a ViewContainerRef manually, or pass in null to stop showing this warning.');
        }

        const dialogReference = this._MatDialog.open(componentType, {
            panelClass: 'dialog-panel-class',
            data: data,
            autoFocus: false,
            restoreFocus: false,
            closeOnNavigation: false,
            viewContainerRef: viewContainerRef
        });

        const componentInstance = dialogReference.componentInstance;
        dialogReference.disableClose = !componentInstance.dismissible;
        if (viewContainerRef) {
            componentInstance['_ViewContainer'] = viewContainerRef;
        }

        if (componentInstance.dismissPrevious) {
            // DismissDialog uses _dialogReference to close the old dialog, not the one we just created
            this.DismissDialog();
        }

        dialogReference.keydownEvents().subscribe(e => {
            if (e.key === 'Escape' && !dialogReference.disableClose) {
                this.DismissDialog();
            }

            let element = e.target as HTMLElement;
            if (
                e.key === 'Enter' &&
                !dialogReference.componentInstance.primaryActionClicked &&
                !dialogReference.componentInstance.secondaryActionClicked &&
                element.nodeName !== 'TEXTAREA' && element.nodeName !== 'INPUT'
            ) {
                dialogReference.componentInstance.PrimaryAction();
            }
        });

        this._dialogReference = dialogReference;

        return componentInstance;
    }

    get dialogReference() {
        return this._dialogReference;
    }

    DismissDialog(): void {
        this._dialogReference?.close();
    }
}
